interface ComparisonRow {
  category: string;
  icon: string;
  start: string | boolean;
  grow: string | boolean;
  transform: string | boolean;
  enterprise: string | boolean;
}

const rows: ComparisonRow[] = [
  {
    category: "Core Features",
    icon: "ri-stack-line",
    start: "Essential modules",
    grow: "All core + advanced modules",
    transform: "Full suite with custom config",
    enterprise: "Complete platform, fully bespoke",
  },
  {
    category: "Module Availability",
    icon: "ri-apps-2-line",
    start: "Up to 3 modules",
    grow: "Up to 8 modules",
    transform: "All BOS modules + OMS + Get Fit",
    enterprise: "Everything, unlimited",
  },
  {
    category: "Reporting & Analytics",
    icon: "ri-bar-chart-2-line",
    start: "Standard reports",
    grow: "Enhanced dashboards",
    transform: "Advanced analytics & custom reports",
    enterprise: "AI-driven insights & executive BI",
  },
  {
    category: "Integrations",
    icon: "ri-plug-line",
    start: "Native integrations only",
    grow: "Standard API access",
    transform: "Custom API integrations",
    enterprise: "Full integration suite + data residency",
  },
  {
    category: "Support Levels",
    icon: "ri-headphone-line",
    start: "Standard email support",
    grow: "Priority chat & email",
    transform: "Dedicated account manager",
    enterprise: "24/7 dedicated success team",
  },
  {
    category: "Training & Onboarding",
    icon: "ri-graduation-cap-line",
    start: "Self-service knowledge base",
    grow: "Guided onboarding sessions",
    transform: "Full team training & onboarding",
    enterprise: "Custom training program & change management",
  },
  {
    category: "Multi-Branch Capability",
    icon: "ri-building-2-line",
    start: "Single location",
    grow: "Up to 3 branches",
    transform: "Multi-branch administration",
    enterprise: "Unlimited branches, global deployment",
  },
  {
    category: "Customization Options",
    icon: "ri-settings-3-line",
    start: "Standard workflows",
    grow: "Customizable workflows",
    transform: "Role-based access & custom workflows",
    enterprise: "White-label, on-premise, full sovereignty",
  },
];

function Cell({ value, isHeader }: { value: string | boolean; isHeader?: boolean }) {
  if (typeof value === "boolean") {
    return (
      <td className={`px-4 py-3.5 text-center ${isHeader ? "" : ""}`}>
        {value ? (
          <span className="w-5 h-5 flex items-center justify-center mx-auto rounded-full bg-emerald-accent/10">
            <i className="ri-check-line text-emerald-accent text-sm" />
          </span>
        ) : (
          <span className="w-5 h-5 flex items-center justify-center mx-auto">
            <i className="ri-close-line text-silver-500 text-sm" />
          </span>
        )}
      </td>
    );
  }
  return (
    <td className="px-4 py-3.5 text-sm text-silver-300 leading-relaxed">
      {value}
    </td>
  );
}

export default function FeatureComparisonTable() {
  return (
    <section className="relative py-16 md:py-24 bg-graphite-900">
      <div className="section-padding">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Compare{" "}
              <span className="text-emerald-accent">Plans</span>
            </h2>
            <p className="text-base sm:text-lg text-silver-300 max-w-2xl mx-auto leading-relaxed">
              See how our plans compare across key dimensions so you can choose the right fit for your organisation.
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden glass-card">
            <table className="w-full">
              <thead>
                <tr className="border-b border-graphite-700/60">
                  <th className="px-5 py-4 text-left text-xs font-semibold text-silver-400 uppercase tracking-wider w-[180px]">
                    Feature
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-white w-[1fr]">
                    Start
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-white bg-emerald-accent/5 border-x border-emerald-accent/15 w-[1fr]">
                    <span className="flex items-center justify-center gap-1.5">
                      Grow
                      <span className="px-2 py-0.5 rounded-full bg-emerald-accent/15 text-emerald-accent text-[10px] font-semibold">
                        Popular
                      </span>
                    </span>
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-white w-[1fr]">
                    Transform
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-white w-[1fr]">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.category}
                    className={`border-b border-graphite-700/30 ${
                      i % 2 === 0 ? "bg-graphite-800/20" : ""
                    }`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-accent/10 shrink-0">
                          <i className={`${row.icon} text-emerald-accent text-sm`} />
                        </span>
                        <span className="text-sm font-medium text-white">{row.category}</span>
                      </div>
                    </td>
                    <Cell value={row.start} />
                    <td className="px-4 py-3.5 text-sm text-silver-300 leading-relaxed bg-emerald-accent/[0.02] border-x border-emerald-accent/10">
                      {row.grow as string}
                    </td>
                    <Cell value={row.transform} />
                    <Cell value={row.enterprise} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {["Start", "Grow", "Transform", "Enterprise"].map((plan, pi) => {
              const isGrow = plan === "Grow";
              return (
                <div
                  key={plan}
                  className={`glass-card p-5 ${
                    isGrow ? "ring-1 ring-emerald-accent/40 border-emerald-border" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <h4 className="text-base font-bold text-white">{plan}</h4>
                    {isGrow && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-accent/15 text-emerald-accent text-[10px] font-semibold">
                        Popular
                      </span>
                    )}
                  </div>
                  <ul className="space-y-3">
                    {rows.map((row) => {
                      const value = pi === 0 ? row.start : pi === 1 ? row.grow : pi === 2 ? row.transform : row.enterprise;
                      return (
                        <li key={row.category} className="flex items-start gap-3">
                          <span className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5 rounded-lg bg-emerald-accent/10">
                            <i className={`${row.icon} text-emerald-accent text-xs`} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-silver-400 block">{row.category}</span>
                            <span className="text-sm text-silver-200">{typeof value === "boolean" ? (value ? "Included" : "Not included") : value}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}